import type { QzConsumerProps, QzOption } from './useQz'
import { useError } from '../useError/index'
import { useObjectState } from '../useObjectState/index'
import qz from 'qz-tray'
import map from 'lodash/map'
import { useCallback, useState } from 'react'
import { useDeepCompareEffect } from '../useDeepCompareEffect'
import isEmpty from 'lodash/isEmpty'

export type Hid = {
  vendorCode: string
  productCode: string
  usagePage: string
  serial: string
  manufacturer: string
  productName: string
  qzOption: QzOption
}

/**
 * This should return all available HID list
 * @constructor
 */
export const useQzHidList = (props: QzConsumerProps): { list: Hid[]; loading: boolean } => {
  const { onError } = useError()
  const { initialize, qzOptions } = props.useQz()

  const [loading, setLoading] = useState(false)

  const [list, setHidList] = useObjectState<Hid[]>()

  const getHidList = useCallback(async () => {
    try {
      setLoading(true)
      if (isEmpty(qzOptions)) return

      const _hids: Hid[] = []

      //we need to be on for of to run the on concurrent
      //we cannot do in parallel because of qzTray instance creation
      for (const qzOption of qzOptions) {
        try {
          await initialize(qzOption)

          const hids = await qz.hid.listDevices()

          const _hidList: Hid[] = map(hids, (item) => {
            return {
              vendorCode: `0x${item.vendorId}`,
              productCode: `0x${item.productId}`,
              usagePage: `0x${item.usagePage}`,
              serial: item.serial,
              manufacturer: item.manufacturer,
              productName: item.product,
              //we just include the host, the certificate and signature will be retrieved from the qzOptions
              qzOption: { host: qzOption.host },
            }
          })

          _hids.push(..._hidList)
        } catch (err) {
          onError(err)
        } finally {
          if (qz.websocket.isActive()) {
            await qz.websocket.disconnect()
          }
        }
      }
      return _hids
    } finally {
      setLoading(false)
    }
  }, [initialize, qzOptions, onError])

  useDeepCompareEffect(() => {
    getHidList().then((_hids) => {
      setHidList(_hids)
    })
  }, [initialize, qzOptions])

  return { list, loading }
}
